import React from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { HERO } from "@/constants/testIds";
import { ArrowDownRight, ArrowRight, Play } from "lucide-react";

// Block 1 — static hero faithful to Ksenia's mockup.
// Character art (transparent cut) layered ABOVE the text so the pointing
// hand stays in front, exactly like the mockup. Transitions come later.
export default function Hero({ onOpenAuth }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Per-language adaptive headline size: RU is short, EN/ES take more space
  // (feedback: Spanish/English look more crowded), so they get a smaller clamp.
  const titleClamp =
    lang === "ru"
      ? "text-[clamp(2.2rem,7vw,7.4rem)]"
      : "text-[clamp(1.9rem,5.8vw,6.2rem)]";

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const startProject = () => {
    if (user && user !== false) navigate("/nuevo-proyecto");
    else if (onOpenAuth) onOpenAuth();
    else scrollTo("contact");
  };

  const lines = (t.hero.title_lines || []).map((w, i) => (
    <span key={i} className="block">
      {w}
    </span>
  ));

  const services = (t.hero.services || "")
    .split("■")
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-[#E9EBEF] grain">
      {/* The character art provides the whole split itself:
          transparent triangle (top-left) lets the hero gray show through,
          the purple region + character + glow are the drawing. Hand crosses
          into the transparent zone so it points in front of the headline. */}
      <img
        src="/hero-character.webp"
        alt=""
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover object-[62%_44%] translate-x-[8%] scale-[0.92] max-md:inset-x-0 max-md:top-0 max-md:bottom-auto max-md:h-[45svh] max-md:object-[62%_22%] max-md:scale-100 max-md:translate-x-0 max-md:translate-y-0 [@media(min-aspect-ratio:2/1)]:object-[62%_52%] [@media(min-aspect-ratio:2/1)]:translate-x-[12%] [@media(min-aspect-ratio:2/1)]:scale-[0.84] z-40 select-none pointer-events-none"
      />

      {/* Black bottom bar with services marquee (in motion, like the original base page) */}
      <div className="absolute bottom-0 left-0 right-0 h-10 bg-[#000000] z-50 overflow-hidden">
        <div className="flex w-max h-full items-center animate-marquee">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className="flex items-center whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.3em] text-white/80"
            >
              {services.map((s, j) => (
                <span key={j} className={`flex items-center ${j === 0 ? "pl-8" : ""}`}>
                  {j > 0 && (
                    <span aria-hidden="true" className="px-5 text-[#8E009C] text-base leading-none select-none">
                      ■
                    </span>
                  )}
                  <span>{s}</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      {/* Corner markers */}
      <div className="absolute top-20 left-6 lg:left-12 font-mono text-[10px] uppercase tracking-[0.3em] text-[#5a5f6b] hidden md:block z-50">
        <div>{t.hero.coords_short}</div>
        <div className="mt-1">ASTANA — KAZAKHSTAN</div>
      </div>
      <div className="absolute top-20 right-6 lg:right-12 font-mono text-[10px] uppercase tracking-[0.3em] text-white/70 hidden md:block text-right z-50">
        <div className="flex items-center gap-2 justify-end">
          <span className="inline-block w-1.5 h-1.5 bg-[#4E53C5] pulse-dot" />
          {t.hero.studio_online} {t.hero.v_year}
        </div>
      </div>

      {/* Services strip (under header) — OCULTA por ahora (repetía los servicios del marquee, feedback Ksenia) */}
      {/* <div className="absolute top-20 left-1/2 -translate-x-1/2 hidden xl:flex font-mono text-[10px] uppercase tracking-[0.25em] text-[#8a8f9a] z-50 whitespace-nowrap">
        {services.map((s, j) => (
          <span key={j} className="flex items-center">
            {j > 0 && (
              <span aria-hidden="true" className="px-3 text-[#8a8f9a] text-sm leading-none select-none">
                ■
              </span>
            )}
            {s}
          </span>
        ))}
      </div> */}

      {/* Text column (below the character layer so the hand stays in front) */}
      <div className="relative z-30 max-w-[1600px] mx-auto px-6 lg:px-12 min-h-[100svh] flex flex-col justify-between pt-24 pb-14 max-md:pt-[48svh]">
        <div className="mt-2 lg:mt-8 max-w-full md:max-w-[52%]">
          <h1 className={`font-display font-bold uppercase tracking-tight leading-[1.02] text-[#3A0F46] ${titleClamp}`}>
            {lines}
          </h1>
          <p className="mt-6 max-w-md font-sans text-sm sm:text-base text-[#3A0F46] leading-relaxed">
            {t.hero.sub}
          </p>
          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8f9a]">
            {t.hero.tags}
          </div>
        </div>

        <div className="pb-2 -translate-y-12 max-md:translate-y-0">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-8">
          {/* Watch portfolio link */}
          <button
            onClick={() => scrollTo("portfolio")}
            data-testid={HERO.ctaPortfolio}
            className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[#000000] hover:text-[#3A0F46] transition-colors"
          >
            <span className="inline-flex items-center justify-center w-8 h-8 bg-[#000000] text-white group-hover:bg-[#3A0F46] transition-colors">
              <Play size={14} fill="currentColor" />
            </span>
            {t.hero.cta_portfolio}
            <ArrowDownRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:translate-y-1" />
          </button>

          {/* Start project button — dark, textured, arrow */}
          <button
            onClick={startProject}
            data-testid={HERO.ctaContact}
            className="group relative inline-flex items-center justify-between gap-5 sm:gap-8 px-5 sm:px-7 py-4 overflow-hidden text-white font-mono text-xs uppercase tracking-[0.2em] border border-[#000000] bg-[#000000]"
          >
            <img
              src="/hero-character.jpg"
              alt=""
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity max-sm:hidden"
            />
            <span className="absolute inset-0 bg-[#000000]/55" />
            <span className="relative z-10">{t.hero.cta_contact}</span>
            <ArrowRight size={16} className="relative z-10 transition-transform group-hover:translate-x-1" />
          </button>
        </div>

        {/* Location line — OCULTA por ahora (feedback: quitar letras pequeñas) */}
        {/* <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#5a5f6b]">
          {t.hero.meta}
        </div> */}
        </div>
      </div>
    </section>
  );
}
